import React from "react"
import { FormattedMessage } from "react-intl"
import { DISABLED_COLOR } from "components/Transactional/Performance/components/Timeline/constants"
import "./index.scss"
import { isGrowth } from "../Tooltip/utils"
import { concatClassNames } from "helpers/jsxHelpers"

interface LegendProps {
	payload?: Payload[]
	onClick: (entry: Payload) => void
	primaryMetric: string
	secondaryMetric: string
	dates: Record<string, string>
	template: string
}

interface Payload {
	inactive?: boolean
	color?: string
	value?: any
	payload?: {
		strokeDasharray?: React.ReactText
	}
}

const METRICS_ID = {
	legend:
		"phronesis.transactional.application.commercial.benchmark.timeline.legend",
	metric:
		"phronesis.transactional.application.commercial.benchmark.timeline.metricsOptions",
	dates: "phronesis.filters.interval.dates",
}

const groupEntriesByColor = (payload: Payload[]): Payload[][] => {
	if (!payload || payload.length === 0) return []

	const groupedMap = createGroupedMap(payload)

	return Array.from(groupedMap.values())
}

const createGroupedMap = (payload: Payload[]): Map<string, Payload[]> => {
	const groupedMap = new Map<string, Payload[]>()

	payload.forEach((legendItem) => {
		if (!legendItem.color) return

		if (!groupedMap.has(legendItem.color)) {
			groupedMap.set(legendItem.color, [])
		}
		groupedMap.get(legendItem.color)!.push(legendItem)
	})

	return groupedMap
}

const Line: React.FC<{
	inactive?: boolean
	strokeDasharray?: React.ReactText
	color?: string
}> = ({ strokeDasharray, color, inactive }) => {
	const strokeColor = inactive ? DISABLED_COLOR : color
	return (
		<svg height="0.5rem" width="2rem">
			<line
				x1="0"
				y1="0.25rem"
				x2="2rem"
				y2="0.25rem"
				strokeDasharray={strokeDasharray}
				stroke={strokeColor}
				strokeWidth="12.5%"
			/>
			<circle cx="1.75rem" cy="0.25rem" r={4} fill={strokeColor} />
		</svg>
	)
}

const GroupLegend: React.FC<{
	values: Payload[]
	onClick: Function
	primaryMetric: string
	secondaryMetric: string
	dates: Record<string, string>
	template?: string
}> = ({
	values,
	onClick,
	primaryMetric,
	secondaryMetric,
	dates,
	template = "",
}) => {
	if (!values.length) return null
	const entryKey = isGrowth(template) ? `_${template}` : ""
	return (
		<div className="group-legend">
			{values.map((entry) => (
				<div
					key={`${entry.color}-${entry.value}`}
					className="legend-item"
					onClick={() => onClick(entry)}
				>
					<Line
						inactive={entry.inactive}
						color={entry.color}
						strokeDasharray={entry.payload?.strokeDasharray}
					/>
					<FormattedMessage
						id={METRICS_ID.legend}
						values={{
							value: entry.value + entryKey,
							secondaryMetric,
							metric: (
								<FormattedMessage
									id={METRICS_ID.metric}
									values={{ metric: primaryMetric }}
								/>
							),
							otherMetric: (
								<FormattedMessage
									id={METRICS_ID.metric}
									values={{ metric: secondaryMetric }}
								/>
							),
							secondaryTrack: (
								<FormattedMessage
									id={METRICS_ID.dates}
									values={{ from: dates.previousFrom, to: dates.previousTo }}
								/>
							),
							primaryTrack: (
								<FormattedMessage
									id={METRICS_ID.dates}
									values={{ from: dates.currentFrom, to: dates.currentTo }}
								/>
							),
						}}
					/>
				</div>
			))}
		</div>
	)
}

const Legend: React.FC<LegendProps> = ({ payload = [], ...props }) => {
	const values = groupEntriesByColor(payload)
	const { template } = props
	return (
		<div className={concatClassNames(["timeline-legend", template])}>
			{values.map((value) => (
				<GroupLegend key={value[0].value} values={value} {...props} />
			))}
		</div>
	)
}

export default Legend
